import gql from 'graphql-tag';
import { ErrorsAttributes } from '../constants';
import { CustomCheckCoreAttributesWithoutSites } from './customChecks';
import { EventCoreAttributesWithContext, buildEventFragment } from './events';

export const GroupAttributesForRouting = `
  id
  name
  sitesCount
  includesAllSitesPages
  allSites
  pinned
`;

export const GroupCoreAttributes = `
id
name
allSites
pinned
pagesCount
assetsCount
pdfsCount
imagesCount
includesAllSitesPages
pageSetRulesAggregator
updatedAt
memberships {
  id
  role
  user {
    id
    name
    email
    memberships {
      account {
        id
      }
      role
    }
  }
}
sites {
  id
  name
  url
}
inclusionPageSetRules {
  id
  operation
  operand
}
exclusionPageSetRules {
  id
  operation
  operand
}
`;

export const GroupCoreAttributesWithoutSites = `
id
name
allSites
pinned
pagesCount
pdfsCount
imagesCount
includesAllSitesPages
pageSetRulesAggregator
updatedAt
memberships {
  id
  role
  user {
    id
    name
    email
    memberships {
      account {
        id
      }
      role
    }
  }
}
inclusionPageSetRules {
  id
  operation
  operand
}
exclusionPageSetRules {
  id
  operation
  operand
}
`;

const GroupDashboardSnapshotFields = `
  id
  score
  trend
  pagesCount
  pdfsCount
  imagesCount
  updatedAt
  accessibility {
    score
    total
    trend
    affectedPagesCount
  }
  badLinks {
    score
    total
    trend
    affectedPagesCount
  }
  spelling {
    score
    total
    trend
    affectedPagesCount
  }
  profanity {
    score
    total
    trend
    affectedPagesCount
  }
  webGovernance {
    score
    total
    trend
    affectedPagesCount
  }
  seo {
    score
    total
    trend
    affectedPagesCount
  }
  bestPractices {
    score
    total
    trend
    affectedPagesCount
  }
`;

const GroupCheckSettingsAttributes = `
  ignoredAccessibility {
    id
    issueId
    help
    elementSource
  }
  ignoredBestPractices {
    id
    issueId
    help
    elementSource
  }
  ignoredSeoIssues {
    id
    help
  }
  ignoredLinks
  ignoredLinkReasons
  ignoredWords
  ignoredProfaneWords
  ignoredCustomCheckIds
  account {
    customChecks {
      ${CustomCheckCoreAttributesWithoutSites}
    }
  }
`;

export const GroupQuery = gql`
  query GroupQuery($groupId: String!, $accountId: String!) {
    group(groupId: $groupId, accountId: $accountId) {
      ${GroupCoreAttributes}
      ${GroupCheckSettingsAttributes}
    }
  }
`;

export const GroupQueryForRouting = gql`
  query GroupQueryForRouting($groupId: String!, $accountId: String!) {
    group(groupId: $groupId, accountId: $accountId) {
      ${GroupAttributesForRouting}
    }
  }
`;

export const GroupDashboardQuery = gql`
  query GroupDashboardQuery($groupId: String!, $accountId: String!) {
    group(groupId: $groupId, accountId: $accountId) {
      ${GroupCoreAttributes}
      a11yCheck
      accessibilityCount
      linkCheck
      badLinksCount
      webGovernanceCheck
      webGovernanceCount
      profanityCheck
      bestPracticesCheck
      bestPracticesCount
      spellCheck
      misspellingsCount
      seoCheck
      seoIssuesCount
      sites {
        id
        name
      }
      latestStatsSnapshot {
        ${GroupDashboardSnapshotFields}
      }
      reportTemplates {
        id
        name
        latestReport(groupId: $groupId) {
          id
          date
          url
        }
      }
      account {
        id
        delinquent
        delinquentReason
      }
    }
  }
`;

export const buildGroupActivityQuery = (attributes = EventCoreAttributesWithContext) => gql`
  query GroupActivityQuery($accountId: String!, $groupId: String!, $page: Int, $perPage: Int, $eventTypes: [String!], $timeframe: Timeframe, $timeframeStart: Date, $timeframeEnd: Date, $userId: String) {
    group(accountId: $accountId, groupId: $groupId) {
      id
      name
      activity(page: $page, perPage: $perPage, eventTypes: $eventTypes, timeframe: $timeframe, timeframeStart: $timeframeStart, timeframeEnd: $timeframeEnd, userId: $userId) {
        currentPage
        perPage
        totalPages
        totalEntries
        nodes {
          ...GroupActivityEventFragment
        }
      }
    }
  }
  ${buildEventFragment(attributes, 'GroupActivityEventFragment')}
`;

export const CreateGroupMutation = gql`
  mutation CreateGroup($accountId: String!, $name: String!, $siteIds: [String!], $includesAllSitesPages: Boolean, $pageSetRulesAggregator: GroupPageSetRuleAggregator, $inclusionPageSetRules: [GroupPageSetRuleInput!], $exclusionPageSetRules: [GroupPageSetRuleInput!], $memberships: [MembershipInput!], $ignoredAccessibility: [IgnoreIssuesInput!], $ignoredBestPractices: [IgnoreIssuesInput!], $ignoredSeoIssues: [String!], $ignoredLinks: [String!], $ignoredLinkReasons: [String!], $ignoredWords: [String!], $ignoredProfaneWords: [String!], $ignoredCustomCheckIds: [String!]) {
    createGroup(accountId: $accountId, name: $name, siteIds: $siteIds, includesAllSitesPages: $includesAllSitesPages, pageSetRulesAggregator: $pageSetRulesAggregator, inclusionPageSetRules: $inclusionPageSetRules, exclusionPageSetRules: $exclusionPageSetRules, memberships: $memberships, ignoredAccessibility: $ignoredAccessibility, ignoredBestPractices: $ignoredBestPractices, ignoredSeoIssues: $ignoredSeoIssues, ignoredLinks: $ignoredLinks, ignoredLinkReasons: $ignoredLinkReasons, ignoredWords: $ignoredWords, ignoredProfaneWords: $ignoredProfaneWords, ignoredCustomCheckIds: $ignoredCustomCheckIds) {
      group {
        ${GroupCoreAttributes}
        ${GroupCheckSettingsAttributes}
        ${ErrorsAttributes}
      }
    }
  }
`;

export const UpdateGroupMutation = gql`
  mutation UpdateGroup($accountId: String!, $groupId: String!, $name: String!, $siteIds: [String!], $includesAllSitesPages: Boolean, $pageSetRulesAggregator: GroupPageSetRuleAggregator, $inclusionPageSetRules: [GroupPageSetRuleInput!], $exclusionPageSetRules: [GroupPageSetRuleInput!], $memberships: [MembershipInput!], $ignoredAccessibility: [IgnoreIssuesInput!], $ignoredBestPractices: [IgnoreIssuesInput!], $ignoredSeoIssues: [String!], $ignoredLinks: [String!], $ignoredLinkReasons: [String!], $ignoredWords: [String!], $ignoredProfaneWords: [String!], $ignoredCustomCheckIds: [String!]) {
    updateGroup(accountId: $accountId, groupId: $groupId, name: $name, siteIds: $siteIds, includesAllSitesPages: $includesAllSitesPages, pageSetRulesAggregator: $pageSetRulesAggregator, inclusionPageSetRules: $inclusionPageSetRules, exclusionPageSetRules: $exclusionPageSetRules, memberships: $memberships, ignoredAccessibility: $ignoredAccessibility, ignoredBestPractices: $ignoredBestPractices, ignoredSeoIssues: $ignoredSeoIssues, ignoredLinks: $ignoredLinks, ignoredLinkReasons: $ignoredLinkReasons, ignoredWords: $ignoredWords, ignoredProfaneWords: $ignoredProfaneWords, ignoredCustomCheckIds: $ignoredCustomCheckIds) {
      group {
        ${GroupCoreAttributes}
        ${GroupCheckSettingsAttributes}
        ${ErrorsAttributes}
      }
    }
  }
`;

export const GroupsQuery = gql`
  query GroupsQuery($accountId: String!, $page: Int!, $perPage: Int!, $sortBy: GroupsSortBy!, $sortOrder: SortOrder!, $search: String, $pinnedOnly: Boolean) {
    groups(accountId: $accountId, page: $page, perPage: $perPage, sortBy: $sortBy, sortOrder: $sortOrder, search: $search, pinnedOnly: $pinnedOnly) {
      currentPage
      perPage
      totalPages
      totalEntries
      nodes {
        ${GroupCoreAttributes}
      }
    }
  }
`;

export const GroupsQueryForRouting = gql`
  query GroupsQueryForRouting($accountId: String!, $page: Int!, $perPage: Int!, $sortBy: GroupsSortBy!, $sortOrder: SortOrder!, $search: String, $pinnedOnly: Boolean) {
    groups(accountId: $accountId, page: $page, perPage: $perPage, sortBy: $sortBy, sortOrder: $sortOrder, search: $search, pinnedOnly: $pinnedOnly) {
      currentPage
      totalPages
      nodes {
        ${GroupAttributesForRouting}
      }
    }
  }
`;

export const GroupsQueryWithSparseSiteMetrics = gql`
  query GroupsQueryWithSparseSiteMetrics($accountId: String!, $page: Int!, $perPage: Int!, $sortBy: GroupsSortBy!, $sortOrder: SortOrder!, $search: String, $pinnedOnly: Boolean) {
    groups(accountId: $accountId, page: $page, perPage: $perPage, sortBy: $sortBy, sortOrder: $sortOrder, search: $search, pinnedOnly: $pinnedOnly) {
      currentPage
      perPage
      totalPages
      totalEntries
      nodes {
        ${GroupCoreAttributesWithoutSites}
        siteMetrics(page: 0, perPage: 11, sortBy: name, sortOrder: desc) {
          nodes {
            id
            siteId
            name
          }
        }
      }
    }
  }
`;

export const GroupsQueryForSelection = gql`
  query GroupsQueryForSelection($accountId: String!, $page: Int!, $perPage: Int!, $sortBy: GroupsSortBy!, $sortOrder: SortOrder!, $search: String, $pinnedOnly: Boolean) {
    groups(accountId: $accountId, page: $page, perPage: $perPage, sortBy: $sortBy, sortOrder: $sortOrder, search: $search, pinnedOnly: $pinnedOnly) {
      currentPage
      perPage
      totalPages
      totalEntries
      nodes {
        id
        name
        allSites
        includesAllSitesPages
        pinned
      }
    }
  }
`;

export const DestroyGroupsMutation = gql`
  mutation DestroyGroups($accountId: String!, $groupIds: [String!]!) {
    destroyGroups(accountId: $accountId, groupIds: $groupIds) {
      status
    }
  }
`;

export const GenerateGroupSnapshotMutation = gql`
  mutation GenerateSnapshot($accountId: String!, $groupId: String!) {
    generateGroupSnapshot(accountId: $accountId, groupId: $groupId) {
      group {
        id
        latestStatsSnapshot {
          ${GroupDashboardSnapshotFields}
        }
      }
    }
  }
`;

export const CreateGroupDependenciesQuery = gql`
  query CreateGroupDependenciesQuery($accountId: String!) {
    customChecks(accountId: $accountId, page: 1, perPage: 100, sortBy: name, sortOrder: asc, includeShared: true) {
      nodes {
        ${CustomCheckCoreAttributesWithoutSites}
      }
    }
  }
`;

export function normalizePageSetRules(rules) {
  return rules.map(rule => {
    const r = { ...rule };

    if (r.id && r.id.startsWith('NEW-')) {
      delete r.id;
    }
    delete r.__typename;

    return r;
  });
}

export const groupRoles = [
  { value: 'viewer', label: 'Viewer' },
  { value: 'manager', label: 'Manager' },
  { value: 'admin', label: 'Admin' },
];

export const groupPageSetRuleDefaults = {
  operation: 'path_starts_with',
  operand: ''
};

export const groupStateDefaults = {
  name: '',
  sites: [],
  memberships: [],
  pageSetRulesAggregator: 'match_all',
  inclusionPageSetRules: [],
  exclusionPageSetRules: [],
  includesAllSitesPages: true,
  ignoredAccessibility: [],
  ignoredBestPractices: [],
  ignoredSeoIssues: [],
  ignoredLinks: [],
  ignoredLinkReasons: [],
  ignoredWords: [],
  ignoredProfaneWords: [],
  ignoredCustomCheckIds: [],
};
